import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { LANGUAGES } from '@/config/languages';
import { usePortalPosition } from '@/hooks/usePortalPosition';
import './LanguageDropdown.css';

// Replaces the legacy 3-button LanguageSwitcher with a popover. Trigger
// shows a globe icon and the current language code (EN/FR/AR); the menu
// is a listbox with a button per language. Persistence + RTL flip are
// already handled by i18n LanguageDetector + the useDirection hook in
// AppProviders, so this component is purely presentational.
export function LanguageDropdown() {
  const { i18n, t } = useTranslation();
  const current = i18n.resolvedLanguage ?? i18n.language ?? 'en';
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const { panelStyle } = usePortalPosition({
    triggerRef,
    open,
    alignment: 'end',
    offset: 6,
    onTriggerOutOfView: () => setOpen(false),
  });

  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: PointerEvent) {
      const target = event.target as Node;
      if (containerRef.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      setOpen(false);
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    }
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const currentLang = LANGUAGES.find((lang) => lang.code === current) ?? LANGUAGES[0];

  function handleSelect(code: string) {
    void i18n.changeLanguage(code);
    setOpen(false);
    triggerRef.current?.focus();
  }

  return (
    <div className="lang-dropdown" ref={containerRef}>
      <button
        type="button"
        ref={triggerRef}
        className="lang-dropdown__trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t('language.label')}
        onClick={() => setOpen((value) => !value)}
      >
        <svg className="lang-dropdown__icon" viewBox="0 0 24 24" aria-hidden="true">
          <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="1.4" />
          <path
            d="M3 12h18M12 3a13 13 0 0 1 0 18M12 3a13 13 0 0 0 0 18"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.4"
          />
        </svg>
        <span className="lang-dropdown__current">{currentLang.code.toUpperCase()}</span>
        <svg
          className="lang-dropdown__chevron"
          data-open={open}
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path d="M6 9l6 6 6-6" fill="none" stroke="currentColor" strokeWidth="1.5" />
        </svg>
      </button>
      {open && typeof document !== 'undefined'
        ? createPortal(
            <div
              ref={menuRef}
              className="lang-dropdown__menu"
              role="listbox"
              aria-label={t('language.label')}
              style={panelStyle}
            >
              {LANGUAGES.map((lang) => {
                const active = lang.code === current;
                return (
                  <button
                    key={lang.code}
                    type="button"
                    role="option"
                    aria-selected={active}
                    className={`lang-dropdown__option${active ? ' is-active' : ''}`}
                    onClick={() => handleSelect(lang.code)}
                  >
                    <span className="lang-dropdown__option-code">{lang.code.toUpperCase()}</span>
                    <span className="lang-dropdown__option-label">{lang.label}</span>
                  </button>
                );
              })}
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
